import jschardet from "jschardet";
import Papa from "papaparse";

export const INVALID_FILE_FORMAT =
	"Invalid file format, please upload a UTF-8 encoded CSV";

/**
 * Read a file and return the content.
 */
export const readFile = async (file: File): Promise<string> => {
	const reader = new FileReader();
	reader.readAsDataURL(file);

	return new Promise<string>((resolve, reject) => {
		reader.onload = () => {
			if (typeof reader.result === "string") {
				resolve(reader.result);
			} else {
				reject(INVALID_FILE_FORMAT);
			}
		};
		reader.onerror = () => {
			reject(reader.error);
		};
	});
};

/**
 * Parses the CSV and returns a list.
 */
const parseCSV = (file: File, encoding: string): Promise<unknown[]> =>
	new Promise<unknown[]>((resolve, reject) => {
		Papa.parse(file, {
			encoding,
			complete: ({ data }) => {
				// data.pop();

				if (data.length === 0) {
					reject(INVALID_FILE_FORMAT);
					return;
				}

				resolve(data);
			},
			error: (err) => {
				reject(err);
			},
		});
	});

/**
 * Get the encoding of a file.
 * @param file The file to get the encoding from.
 * @returns The encoding of the file.
 */
export const getEncoding = (
	fileResult: string | ArrayBuffer | null,
): { encoding: string; error?: string } => {
	if (fileResult === null) {
		return { encoding: "", error: "Data was null" };
	}

	// fail if fileResult is an array buffer
	if (fileResult instanceof ArrayBuffer) {
		return { encoding: "", error: "Data was an array buffer" };
	}

	const parts = fileResult.split(";base64,");
	if (parts.length !== 2) {
		return { encoding: "", error: "Data was not in the expected format" };
	}

	// convert to binary
	const data = parts[1]!;
	const bin = atob(data);

	// get encoding
	const { encoding } = jschardet.detect(bin);
	if (encoding === "windows-1252") {
		return { encoding: "ANSI" };
	}
	return { encoding };
};

// Windows changes the mimetype to include excel, which is technically
// incorrect.
const validFileTypes = [/.csv/, /.excel/];

/**
 * Parse a file and return a list of items.
 * @param file The file to parse.
 */
export const parseFile = async (
	file: File,
): Promise<{ items: unknown[]; error?: string }> => {
	// Go through the valid regexes, and check that the file has a valid mime type.
	if (!validFileTypes.some((type) => type.exec(file.type))) {
		return { items: [], error: INVALID_FILE_FORMAT };
	}

	// Get file data content. This will be used for detecting the encoding.
	let fileData: string;
	try {
		fileData = await readFile(file);
	} catch (e) {
		return { items: [], error: e instanceof Error ? e.message : (e as string) };
	}

	// Detect encoding
	const { encoding, error } = getEncoding(fileData);
	if (error) {
		return { items: [], error };
	}

	// Parse the file
	try {
		const items = await parseCSV(file, encoding);
		return { items };
	} catch (e) {
		return { items: [], error: e instanceof Error ? e.message : (e as string) };
	}
};

interface FileResult<T> {
	rows: T[];
	error?: string;
}

export type ToRowType<T> = (item: unknown) => { row: T | null; error?: string };

/**
 * Parse a file and return a list of rows.
 * @param file The file to parse.
 * @param role The role to assign to the rows.
 */
const getRowsFromSingleFile = async <T>(
	file: File,
	toRow: ToRowType<T>,
): Promise<FileResult<T>> => {
	const rows: T[] = [];
	const { items, error } = await parseFile(file);
	if (error) {
		return { rows, error };
	}

	// Convert the items to rows
	for (const item of items) {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { row, error } = toRow(item);
		if (error) {
			return { rows, error };
		}

		if (row) {
			rows.push(row);
		}
	}
	return { rows };
};

export interface MultipleFileResults<T> {
	rows: T[];
	error?: string;
}

/**
 * Gets all the typed rows from the given files.
 */
export const getRowsFromFiles = async <T>(
	files: File[],
	toRow: ToRowType<T>,
): Promise<MultipleFileResults<T>> => {
	const filesResults: Promise<FileResult<T>>[] = [];

	// Handle all the input files concurrently
	for (const file of files) {
		filesResults.push(getRowsFromSingleFile(file, toRow));
	}

	// Get the typed rows from the results
	const allRows: T[] = [];
	for (const { rows, error } of await Promise.all(filesResults)) {
		if (error) {
			return { rows: [], error };
		}

		allRows.push(...rows);
	}

	return { rows: allRows };
};
